import { withEventMeta, type EventInput } from "./events";
import { selectSettlementSnapshot, selectSupplierPayout } from "./selectors";
import type { YJMockData } from "./seed";
import {
  brandId,
  brokerId,
  customerId,
  leadId,
  orderId,
  productId,
  ticketId,
  type Brand,
  type BrandId,
  type BrokerId,
  type Campaign,
  type Coupon,
  type Customer,
  type EntityCounters,
  type Lead,
  type LeadId,
  type Order,
  type PaymentMethod,
  type Product,
  type ProductId,
  type TicketId,
} from "./types";

type CounterKey = keyof EntityCounters;

export class YJDomainError extends Error {
  constructor(
    message: string,
    readonly code:
      | "NOT_FOUND"
      | "OUT_OF_STOCK"
      | "INVALID_AMOUNT"
      | "PAYMENT_FAILED"
      | "PDPA_DECLINED"
      | "INVALID_STATE",
  ) {
    super(message);
    this.name = "YJDomainError";
  }
}

export type ActionResult<T> = {
  state: YJMockData;
  data: T;
};

function advanceClock(state: YJMockData, multiplier = 1) {
  const at = state.clock.now + state.clock.stepMs * multiplier;
  return [{ ...state, clock: { ...state.clock, now: at } }, at] as const;
}

function nextCounter(state: YJMockData, key: CounterKey) {
  const next = state.counters[key] + 1;
  return [{ ...state, counters: { ...state.counters, [key]: next } }, next] as const;
}

function nextCustomerId(state: YJMockData) {
  const [next, value] = nextCounter(state, "customer");
  return [next, customerId(`c${value}`)] as const;
}

function nextProductId(state: YJMockData) {
  const [next, value] = nextCounter(state, "product");
  return [next, productId(`p${value}`)] as const;
}

function nextOrderId(state: YJMockData) {
  const [next, value] = nextCounter(state, "order");
  return [next, orderId(`o${value}`)] as const;
}

function nextLeadId(state: YJMockData, id: Order["id"]) {
  const [next] = nextCounter(state, "lead");
  return [next, leadId(`L_${id}`)] as const;
}

function nextCouponId(state: YJMockData) {
  const [next, value] = nextCounter(state, "coupon");
  return [next, `cp${value}`] as const;
}

function nextCampaignId(state: YJMockData) {
  const [next, value] = nextCounter(state, "campaign");
  return [next, `cm${value}`] as const;
}

function nextPushId(state: YJMockData) {
  const [next, value] = nextCounter(state, "push");
  return [next, `n${value}`] as const;
}

function appendEvent(state: YJMockData, input: EventInput) {
  const [next, counter] = nextCounter(state, "event");
  return {
    ...next,
    events: [...next.events, withEventMeta(`evt_${String(counter).padStart(4, "0")}`, input)],
  };
}

function syncSettlement(state: YJMockData) {
  const snapshot = selectSettlementSnapshot(state);
  const exists = state.settlements.some((settlement) => settlement.id === snapshot.id);
  return {
    ...state,
    settlements: exists
      ? state.settlements.map((settlement) => (settlement.id === snapshot.id ? snapshot : settlement))
      : [snapshot, ...state.settlements],
  };
}

function requireProduct(state: YJMockData, id: ProductId) {
  const product = state.products.find((item) => item.id === id);
  if (!product) throw new YJDomainError("ไม่พบสินค้า", "NOT_FOUND");
  return product;
}

function requireLead(state: YJMockData, id: LeadId) {
  const lead = state.leads.find((item) => item.id === id);
  if (!lead) throw new YJDomainError("ไม่พบลีด", "NOT_FOUND");
  return lead;
}

function requireBrand(state: YJMockData, id: BrandId) {
  const brand = state.brands.find((item) => item.id === id);
  if (!brand) throw new YJDomainError("ไม่พบแบรนด์", "NOT_FOUND");
  return brand;
}

export function placeOrder(
  state: YJMockData,
  input: { customerId?: Customer["id"]; productId: ProductId; brokerId?: BrokerId; paymentMethod: PaymentMethod; customer?: Omit<Customer, "id"> },
): ActionResult<{ order: Order; lead: Lead }> {
  let next = state;
  const product = requireProduct(next, input.productId);
  if (product.stock <= 0) throw new YJDomainError("สินค้าหมด", "OUT_OF_STOCK");

  let cid = input.customerId;
  if (!cid && input.customer) {
    const customerName = input.customer.name.trim();
    if (!customerName) throw new YJDomainError("ข้อมูลลูกค้าไม่ครบ", "INVALID_STATE");
    [next, cid] = nextCustomerId(next);
    next = {
      ...next,
      customers: [{ id: cid, ...input.customer }, ...next.customers],
    };
  }
  if (!cid) throw new YJDomainError("ต้องมีลูกค้า", "INVALID_STATE");

  const clock = advanceClock(next);
  next = clock[0];
  const at = clock[1];
  let newOrderId;
  [next, newOrderId] = nextOrderId(next);
  let newLeadId;
  [next, newLeadId] = nextLeadId(next, newOrderId);

  const order: Order = {
    id: newOrderId,
    customerId: cid,
    productId: product.id,
    productName: product.name,
    brokerId: input.brokerId ?? null,
    brandId: product.brandId,
    maai: 1,
    amount: product.price,
    status: input.paymentMethod === "COD" ? "pending" : "paid",
    paymentMethod: input.paymentMethod,
    isHouseBrand: next.brands.find((brand) => brand.id === product.brandId)?.isHouse,
    createdAt: at,
  };

  const lead: Lead = {
    id: newLeadId,
    orderId: order.id,
    customerId: cid,
    productId: product.id,
    brokerId: input.brokerId ?? null,
    status: "pending",
    assignedTo: null,
    createdAt: at + next.clock.stepMs,
    attempts: 0,
  };

  next = {
    ...next,
    orders: [order, ...next.orders],
    leads: [lead, ...next.leads],
    products: next.products.map((item) =>
      item.id === product.id ? { ...item, stock: Math.max(0, item.stock - 1), sold: item.sold + 1 } : item,
    ),
  };
  next = appendEvent(next, {
    type: "order.maai1.created",
    orderId: order.id,
    customerId: order.customerId,
    productId: order.productId,
    brokerId: order.brokerId,
    amount: order.amount,
    at,
  });
  return { state: syncSettlement(next), data: { order, lead } };
}

export function assignLead(state: YJMockData, leadIdInput: LeadId, agent: string): ActionResult<Lead> {
  let next = state;
  const lead = requireLead(next, leadIdInput);
  const clock = advanceClock(next);
  next = clock[0];
  const at = clock[1];
  const updated: Lead = { ...lead, status: "calling", assignedTo: agent, attempts: lead.attempts + 1 };
  next = {
    ...next,
    leads: next.leads.map((item) => (item.id === leadIdInput ? updated : item)),
  };
  next = appendEvent(next, { type: "lead.assigned", leadId: leadIdInput, agent, at });
  return { state: next, data: updated };
}

export function closeUpsell(
  state: YJMockData,
  input: { leadId: LeadId; productId: ProductId; useHouseBrand: boolean; discount: number },
): ActionResult<Order> {
  let next = state;
  const lead = requireLead(next, input.leadId);
  const product = requireProduct(next, input.productId);
  if (product.stock <= 0) throw new YJDomainError("สินค้าหมด", "OUT_OF_STOCK");
  const clock = advanceClock(next);
  next = clock[0];
  const at = clock[1];
  let newOrderId;
  [next, newOrderId] = nextOrderId(next);
  const amount = Math.max(0, product.price - input.discount);
  const order: Order = {
    id: newOrderId,
    customerId: lead.customerId,
    productId: product.id,
    productName: product.name,
    brokerId: lead.brokerId,
    brandId: product.brandId,
    maai: 2,
    amount,
    status: "paid",
    paymentMethod: "TELESALES",
    isHouseBrand: input.useHouseBrand,
    createdAt: at,
  };
  next = {
    ...next,
    orders: [order, ...next.orders],
    leads: next.leads.map((item) => (item.id === lead.id ? { ...item, status: "closed", closedAt: at } : item)),
    products: next.products.map((item) =>
      item.id === product.id ? { ...item, stock: Math.max(0, item.stock - 1), sold: item.sold + 1 } : item,
    ),
  };
  next = appendEvent(next, {
    type: "order.maai2.closed",
    orderId: order.id,
    leadId: lead.id,
    amount,
    useHouseBrand: input.useHouseBrand,
    at,
  });
  return { state: syncSettlement(next), data: order };
}

export function rejectLead(state: YJMockData, leadIdInput: LeadId, reason: string): ActionResult<Lead> {
  let next = state;
  const lead = requireLead(next, leadIdInput);
  const clock = advanceClock(next);
  next = clock[0];
  const at = clock[1];
  const updated: Lead = { ...lead, status: "rejected", reason, closedAt: at };
  next = {
    ...next,
    leads: next.leads.map((item) => (item.id === leadIdInput ? updated : item)),
  };
  next = appendEvent(next, { type: "lead.rejected", leadId: leadIdInput, reason, at });
  return { state: next, data: updated };
}

export function brokerWithdraw(state: YJMockData, id: BrokerId, amount: number): ActionResult<void> {
  if (amount <= 0) throw new YJDomainError("จำนวนเงินไม่ถูกต้อง", "INVALID_AMOUNT");
  let next = state;
  const clock = advanceClock(next);
  next = clock[0];
  const at = clock[1];
  next = {
    ...next,
    brokers: next.brokers.map((broker) => (broker.id === id ? { ...broker, withdrawn: broker.withdrawn + amount } : broker)),
  };
  next = appendEvent(next, { type: "broker.withdraw", brokerId: id, amount, at });
  return { state: next, data: undefined };
}

export function supplierWithdraw(state: YJMockData, id: BrandId, amount: number): ActionResult<void> {
  if (amount <= 0) throw new YJDomainError("จำนวนเงินไม่ถูกต้อง", "INVALID_AMOUNT");
  let next = state;
  const brand = requireBrand(next, id);
  const payout = selectSupplierPayout(next, id);
  if (!payout) throw new YJDomainError("ไม่พบยอดจ่าย", "NOT_FOUND");
  const clock = advanceClock(next);
  next = clock[0];
  const at = clock[1];
  const ref = `WD-${String(next.counters.event + 1).padStart(6, "0")}`;
  next = {
    ...next,
    supplierPayouts: next.supplierPayouts.map((item) =>
      item.brandId === id
        ? {
            ...item,
            withdrawn: item.withdrawn + amount,
            history: [{ d: at, a: amount, ref, status: "pending" }, ...(item.history ?? [])],
          }
        : item,
    ),
  };
  if (!next.supplierPayouts.some((item) => item.brandId === id)) {
    next = {
      ...next,
      supplierPayouts: [
        { brandId: id, gross: payout.gross, supplierShare: payout.supplierShare, withdrawn: amount, history: [{ d: at, a: amount, ref, status: "pending" }] },
        ...next.supplierPayouts,
      ],
    };
  }
  next = appendEvent(next, { type: "supplier.withdraw", brandId: brand.id, amount, at });
  return { state: next, data: undefined };
}

export function payDividend(state: YJMockData, settlementId: string): ActionResult<ReturnType<typeof selectSettlementSnapshot>> {
  let next = state;
  const clock = advanceClock(next);
  next = clock[0];
  const at = clock[1];
  const snapshot = selectSettlementSnapshot(next, settlementId);
  const paid = { ...snapshot, paidOut: true, paidAt: at };
  next = {
    ...next,
    settlements: next.settlements.some((settlement) => settlement.id === settlementId)
      ? next.settlements.map((settlement) => (settlement.id === settlementId ? paid : settlement))
      : [paid, ...next.settlements],
  };
  next = appendEvent(next, { type: "settlement.paid", settlementId, at });
  return { state: next, data: paid };
}

export function upsertProduct(
  state: YJMockData,
  input: Partial<Product> & { brandId: BrandId; name: string; price: number },
): ActionResult<Product> {
  let next = state;
  const existing = input.id ? next.products.find((product) => product.id === input.id) : null;
  const product: Product = existing
    ? { ...existing, ...input, id: existing.id }
    : (() => {
        let newId;
        [next, newId] = nextProductId(next);
        return {
          id: newId,
          brandId: input.brandId,
          name: input.name,
          price: input.price,
          stock: input.stock ?? 0,
          sold: input.sold ?? 0,
          telesalesDiscount: input.telesalesDiscount ?? 0,
          description: input.description,
        };
      })();
  next = {
    ...next,
    products: existing ? next.products.map((item) => (item.id === product.id ? product : item)) : [product, ...next.products],
  };
  return { state: next, data: product };
}

export function deleteProduct(state: YJMockData, id: ProductId): ActionResult<void> {
  return {
    state: { ...state, products: state.products.filter((product) => product.id !== id) },
    data: undefined,
  };
}

export function approveBrand(state: YJMockData, id: BrandId): ActionResult<Brand> {
  let next = state;
  const brand = requireBrand(next, id);
  const clock = advanceClock(next);
  next = clock[0];
  const at = clock[1];
  const updated: Brand = { ...brand, status: "active" };
  next = {
    ...next,
    brands: next.brands.map((item) => (item.id === id ? updated : item)),
  };
  next = appendEvent(next, { type: "brand.approved", brandId: id, at });
  return { state: next, data: updated };
}

export function toggleBrandStatus(state: YJMockData, id: BrandId): ActionResult<Brand> {
  const brand = requireBrand(state, id);
  const updated: Brand = { ...brand, status: brand.status === "active" ? "suspended" : "active" };
  return {
    state: { ...state, brands: state.brands.map((item) => (item.id === id ? updated : item)) },
    data: updated,
  };
}

export function addBrand(state: YJMockData, input: Omit<Brand, "id" | "joined" | "status" | "isHouse">): ActionResult<Brand> {
  let next = state;
  const clock = advanceClock(next);
  next = clock[0];
  const at = clock[1];
  const [withCounter, counter] = nextCounter(next, "brand");
  next = withCounter;
  const brand: Brand = { id: brandId(`b${counter}`), status: "active", joined: at, isHouse: false, ...input };
  return { state: { ...next, brands: [brand, ...next.brands] }, data: brand };
}

export function addCoupon(
  state: YJMockData,
  input: Omit<Coupon, "id" | "used" | "status" | "createdAt">,
): ActionResult<Coupon> {
  let next = state;
  const clock = advanceClock(next);
  next = clock[0];
  const at = clock[1];
  let id;
  [next, id] = nextCouponId(next);
  const coupon: Coupon = { id, used: 0, status: "active", createdAt: at, ...input };
  next = { ...next, coupons: [coupon, ...next.coupons] };
  next = appendEvent(next, { type: "coupon.added", couponId: id, code: coupon.code, at });
  return { state: next, data: coupon };
}

export function addCampaign(
  state: YJMockData,
  input: Omit<Campaign, "id" | "spent" | "gmv" | "status" | "startsAt">,
): ActionResult<Campaign> {
  let next = state;
  const clock = advanceClock(next);
  next = clock[0];
  const at = clock[1];
  let id;
  [next, id] = nextCampaignId(next);
  const campaign: Campaign = { id, spent: 0, gmv: 0, status: "active", startsAt: at, ...input };
  next = { ...next, campaigns: [campaign, ...next.campaigns] };
  next = appendEvent(next, { type: "campaign.added", campaignId: id, name: campaign.name, at });
  return { state: syncSettlement(next), data: campaign };
}

export function sendPush(state: YJMockData, input: { title: string; body: string }): ActionResult<void> {
  let next = state;
  const clock = advanceClock(next);
  next = clock[0];
  const at = clock[1];
  let id;
  [next, id] = nextPushId(next);
  const reach = next.brokers.length;
  next = { ...next, pushLog: [{ id, title: input.title, body: input.body, sentAt: at, reach }, ...next.pushLog] };
  next = appendEvent(next, { type: "push.sent", title: input.title, reach, at });
  return { state: next, data: undefined };
}

export function replyTicket(state: YJMockData, id: TicketId, text: string, from = "support"): ActionResult<void> {
  let next = state;
  const clock = advanceClock(next);
  next = clock[0];
  const at = clock[1];
  next = {
    ...next,
    tickets: next.tickets.map((ticket) =>
      ticket.id === id
        ? {
            ...ticket,
            messages: [...ticket.messages, { from: from as "support", text, at }],
            status: from === "support" ? "in_progress" : ticket.status,
          }
        : ticket,
    ),
  };
  next = appendEvent(next, { type: "ticket.replied", ticketId: id, from, at });
  return { state: next, data: undefined };
}

export function closeTicket(state: YJMockData, id: TicketId): ActionResult<void> {
  let next = state;
  const clock = advanceClock(next);
  next = clock[0];
  const at = clock[1];
  next = {
    ...next,
    tickets: next.tickets.map((ticket) => (ticket.id === id ? { ...ticket, status: "closed" } : ticket)),
  };
  next = appendEvent(next, { type: "ticket.closed", ticketId: id, at });
  return { state: next, data: undefined };
}

export function moderatePost(state: YJMockData, postId: string, status: "removed" | "ignored"): ActionResult<void> {
  return {
    state: {
      ...state,
      flaggedPosts: state.flaggedPosts.map((post) => (post.id === postId ? { ...post, status } : post)),
    },
    data: undefined,
  };
}

export function switchBroker(state: YJMockData, id: string): ActionResult<void> {
  return { state: { ...state, currentBrokerId: brokerId(id) }, data: undefined };
}

export function switchBrand(state: YJMockData, id: string): ActionResult<void> {
  return { state: { ...state, currentBrandId: brandId(id) }, data: undefined };
}

export function updateSettings(state: YJMockData, patch: Partial<YJMockData["settings"]>): ActionResult<void> {
  return { state: { ...state, settings: { ...state.settings, ...patch } }, data: undefined };
}

export const id = {
  brand: brandId,
  broker: brokerId,
  customer: customerId,
  lead: leadId,
  order: orderId,
  product: productId,
  ticket: ticketId,
};
